/**
 * Pixel-stretch collage: ordered crop nodes connected by edge-extruded streaks.
 */

export interface PixelRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface PixelStretchNode {
    id: string;
    /** Region in source image pixels. */
    crop: PixelRect;
    /** Placement on output artboard. */
    frame: PixelRect;
}

export type StretchEdge = "left" | "right" | "top" | "bottom";

export interface PixelStretchArtboard {
    width: number;
    height: number;
    /** CSS-style hex, e.g. #ffffff */
    backgroundColor: string;
}

export interface PixelStretchRenderOptions {
    /** Max samples per connector edge (quality cap). */
    connectorMaxSamples?: number;
    /** Skip artboard fill so a dimmed source can show through (preview only). */
    transparentBackground?: boolean;
}

const DEFAULT_CONNECTOR_MAX_SAMPLES = 256;

export function rectCenter(r: PixelRect): { x: number; y: number } {
    return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

/** Pick out/in edges from relative frame positions. */
export function pickConnectorEdges(
    frameA: PixelRect,
    frameB: PixelRect,
): { out: StretchEdge; in: StretchEdge } {
    const ca = rectCenter(frameA);
    const cb = rectCenter(frameB);
    const dx = cb.x - ca.x;
    const dy = cb.y - ca.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
        return dx > 0
            ? { out: "right", in: "left" }
            : { out: "left", in: "right" };
    }
    return dy > 0
        ? { out: "bottom", in: "top" }
        : { out: "top", in: "bottom" };
}

export interface Point2 {
    x: number;
    y: number;
}

/** Edge segment endpoints in artboard space (consistent top→bottom or left→right). */
export function edgeSegment(frame: PixelRect, edge: StretchEdge): [Point2, Point2] {
    const { x, y, w, h } = frame;
    switch (edge) {
        case "left":
            return [
                { x, y },
                { x, y: y + h },
            ];
        case "right":
            return [
                { x: x + w, y },
                { x: x + w, y: y + h },
            ];
        case "top":
            return [
                { x, y },
                { x: x + w, y },
            ];
        case "bottom":
            return [
                { x, y: y + h },
                { x: x + w, y: y + h },
            ];
    }
}

function edgeLength(a: Point2, b: Point2): number {
    return Math.hypot(b.x - a.x, b.y - a.y);
}

function lerpPoint(a: Point2, b: Point2, t: number): Point2 {
    return {
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
    };
}

function parseHexColor(hex: string): [number, number, number, number] {
    const h = hex.replace(/^#/, "");
    if (h.length === 3) {
        const r = parseInt(h[0] + h[0], 16);
        const g = parseInt(h[1] + h[1], 16);
        const b = parseInt(h[2] + h[2], 16);
        return [r, g, b, 255];
    }
    if (h.length >= 6) {
        return [
            parseInt(h.slice(0, 2), 16),
            parseInt(h.slice(2, 4), 16),
            parseInt(h.slice(4, 6), 16),
            255,
        ];
    }
    return [255, 255, 255, 255];
}

export function sampleCountForEdge(
    crop: PixelRect,
    edge: StretchEdge,
    frameEdge: [Point2, Point2],
    maxSamples: number,
): number {
    const cropLen =
        edge === "left" || edge === "right" ? crop.h : crop.w;
    const frameLen = edgeLength(frameEdge[0], frameEdge[1]);
    const n = Math.max(2, Math.ceil(Math.max(cropLen, frameLen)));
    return Math.min(maxSamples, n);
}

/** Sample colors along the crop edge in source image space. */
export function extractEdgePixels(
    imageData: ImageData,
    crop: PixelRect,
    edge: StretchEdge,
    sampleCount: number,
): Uint8ClampedArray {
    const { data, width: imgW } = imageData;
    const out = new Uint8ClampedArray(sampleCount * 4);
    const n = Math.max(1, sampleCount);

    for (let i = 0; i < n; i++) {
        const t = n === 1 ? 0 : i / (n - 1);
        let sx: number;
        let sy: number;
        switch (edge) {
            case "left":
                sx = crop.x;
                sy = crop.y + t * Math.max(0, crop.h - 1);
                break;
            case "right":
                sx = crop.x + Math.max(0, crop.w - 1);
                sy = crop.y + t * Math.max(0, crop.h - 1);
                break;
            case "top":
                sx = crop.x + t * Math.max(0, crop.w - 1);
                sy = crop.y;
                break;
            case "bottom":
                sx = crop.x + t * Math.max(0, crop.w - 1);
                sy = crop.y + Math.max(0, crop.h - 1);
                break;
        }
        const ix = Math.min(imgW - 1, Math.max(0, Math.round(sx)));
        const iy = Math.min(
            imageData.height - 1,
            Math.max(0, Math.round(sy)),
        );
        const si = (iy * imgW + ix) * 4;
        const oi = i * 4;
        out[oi] = data[si];
        out[oi + 1] = data[si + 1];
        out[oi + 2] = data[si + 2];
        out[oi + 3] = data[si + 3];
    }
    return out;
}

function fillQuadOnImageData(
    buf: Uint8ClampedArray,
    bufW: number,
    bufH: number,
    p0: Point2,
    p1: Point2,
    p2: Point2,
    p3: Point2,
    color: [number, number, number, number],
): void {
    const minX = Math.max(
        0,
        Math.floor(Math.min(p0.x, p1.x, p2.x, p3.x)),
    );
    const maxX = Math.min(
        bufW - 1,
        Math.ceil(Math.max(p0.x, p1.x, p2.x, p3.x)),
    );
    const minY = Math.max(
        0,
        Math.floor(Math.min(p0.y, p1.y, p2.y, p3.y)),
    );
    const maxY = Math.min(
        bufH - 1,
        Math.ceil(Math.max(p0.y, p1.y, p2.y, p3.y)),
    );

    for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
            if (pointInQuad(x + 0.5, y + 0.5, p0, p1, p2, p3)) {
                const i = (y * bufW + x) * 4;
                buf[i] = color[0];
                buf[i + 1] = color[1];
                buf[i + 2] = color[2];
                buf[i + 3] = color[3];
            }
        }
    }
}

function pointInQuad(
    px: number,
    py: number,
    p0: Point2,
    p1: Point2,
    p2: Point2,
    p3: Point2,
): boolean {
    return (
        pointInTri(px, py, p0, p1, p2) || pointInTri(px, py, p0, p2, p3)
    );
}

function pointInTri(
    px: number,
    py: number,
    a: Point2,
    b: Point2,
    c: Point2,
): boolean {
    const v0x = c.x - a.x;
    const v0y = c.y - a.y;
    const v1x = b.x - a.x;
    const v1y = b.y - a.y;
    const v2x = px - a.x;
    const v2y = py - a.y;
    const dot00 = v0x * v0x + v0y * v0y;
    const dot01 = v0x * v1x + v0y * v1y;
    const dot02 = v0x * v2x + v0y * v2y;
    const dot11 = v1x * v1x + v1y * v1y;
    const dot12 = v1x * v2x + v1y * v2y;
    const invDenom = 1 / (dot00 * dot11 - dot01 * dot01 + 1e-12);
    const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom;
    return u >= 0 && v >= 0 && u + v <= 1;
}

/** Render connector from node A's out edge to node B's in edge. */
export function renderStretchConnector(
    buf: Uint8ClampedArray,
    bufW: number,
    bufH: number,
    imageData: ImageData,
    nodeA: PixelStretchNode,
    outEdge: StretchEdge,
    nodeB: PixelStretchNode,
    inEdge: StretchEdge,
    maxSamples: number,
): void {
    const aSeg = edgeSegment(nodeA.frame, outEdge);
    const bSeg = edgeSegment(nodeB.frame, inEdge);
    const n = sampleCountForEdge(nodeA.crop, outEdge, aSeg, maxSamples);
    const samples = extractEdgePixels(imageData, nodeA.crop, outEdge, n);

    for (let i = 0; i < n; i++) {
        const t0 = n === 1 ? 0 : i / (n - 1);
        const t1 = n === 1 ? 1 : (i + 1) / (n - 1);
        const pA0 = lerpPoint(aSeg[0], aSeg[1], t0);
        const pA1 = lerpPoint(aSeg[0], aSeg[1], t1);
        const pB0 = lerpPoint(bSeg[0], bSeg[1], t0);
        const pB1 = lerpPoint(bSeg[0], bSeg[1], t1);
        const oi = i * 4;
        const color: [number, number, number, number] = [
            samples[oi],
            samples[oi + 1],
            samples[oi + 2],
            samples[oi + 3],
        ];
        fillQuadOnImageData(buf, bufW, bufH, pA0, pB0, pB1, pA1, color);
    }
}

export function drawNodeOnContext(
    ctx: CanvasRenderingContext2D,
    image: CanvasImageSource,
    node: PixelStretchNode,
): void {
    const { crop, frame } = node;
    if (crop.w <= 0 || crop.h <= 0 || frame.w <= 0 || frame.h <= 0) return;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(
        image,
        crop.x,
        crop.y,
        crop.w,
        crop.h,
        frame.x,
        frame.y,
        frame.w,
        frame.h,
    );
}

/**
 * Composite ordered nodes + connectors onto a canvas.
 * Returns the canvas (caller may read ImageData or toDataURL).
 */
export function renderPixelStretchComposite(
    image: CanvasImageSource,
    imageData: ImageData,
    nodes: PixelStretchNode[],
    artboard: PixelStretchArtboard,
    options: PixelStretchRenderOptions = {},
): HTMLCanvasElement {
    const maxSamples =
        options.connectorMaxSamples ?? DEFAULT_CONNECTOR_MAX_SAMPLES;
    const W = Math.max(1, Math.round(artboard.width));
    const H = Math.max(1, Math.round(artboard.height));

    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    if (!ctx) return canvas;

    const transparentBg = options.transparentBackground === true;
    if (!transparentBg) {
        const bg = parseHexColor(artboard.backgroundColor);
        ctx.fillStyle = `rgba(${bg[0]},${bg[1]},${bg[2]},${bg[3] / 255})`;
        ctx.fillRect(0, 0, W, H);
    }

    const buf = new Uint8ClampedArray(W * H * 4);
    if (!transparentBg) {
        const bgBuf = parseHexColor(artboard.backgroundColor);
        for (let i = 0; i < W * H; i++) {
            const j = i * 4;
            buf[j] = bgBuf[0];
            buf[j + 1] = bgBuf[1];
            buf[j + 2] = bgBuf[2];
            buf[j + 3] = bgBuf[3];
        }
    }

    for (let i = 0; i < nodes.length; i++) {
        if (i > 0) {
            const a = nodes[i - 1];
            const b = nodes[i];
            const { out, in: inEdge } = pickConnectorEdges(a.frame, b.frame);
            renderStretchConnector(
                buf,
                W,
                H,
                imageData,
                a,
                out,
                b,
                inEdge,
                maxSamples,
            );
        }
    }

    const stretchLayer = ctx.createImageData(W, H);
    stretchLayer.data.set(buf);
    ctx.putImageData(stretchLayer, 0, 0);

    for (const node of nodes) {
        drawNodeOnContext(ctx, image, node);
    }

    return canvas;
}

/** Source UV corners for a 1px strip at `edge` of `crop` in full image space. */
export function cropEdgeSourceQuad(
    crop: PixelRect,
    edge: StretchEdge,
    imgW: number,
    imgH: number,
): [Point2, Point2, Point2, Point2] {
    const x0 = crop.x;
    const y0 = crop.y;
    const x1 = crop.x + crop.w;
    const y1 = crop.y + crop.h;
    const clamp = (p: Point2): Point2 => ({
        x: Math.min(imgW, Math.max(0, p.x)),
        y: Math.min(imgH, Math.max(0, p.y)),
    });
    switch (edge) {
        case "left":
            return [
                clamp({ x: x0, y: y0 }),
                clamp({ x: x0, y: y1 }),
                clamp({ x: x0 + 1, y: y1 }),
                clamp({ x: x0 + 1, y: y0 }),
            ];
        case "right":
            return [
                clamp({ x: x1 - 1, y: y0 }),
                clamp({ x: x1, y: y0 }),
                clamp({ x: x1, y: y1 }),
                clamp({ x: x1 - 1, y: y1 }),
            ];
        case "top":
            return [
                clamp({ x: x0, y: y0 }),
                clamp({ x: x1, y: y0 }),
                clamp({ x: x1, y: y0 + 1 }),
                clamp({ x: x0, y: y0 + 1 }),
            ];
        case "bottom":
            return [
                clamp({ x: x0, y: y1 - 1 }),
                clamp({ x: x0, y: y1 }),
                clamp({ x: x1, y: y1 }),
                clamp({ x: x1, y: y1 - 1 }),
            ];
    }
}

/** Destination quad for connector between frame edges. */
export function frameEdgeDestQuad(
    frameA: PixelRect,
    outEdge: StretchEdge,
    frameB: PixelRect,
    inEdge: StretchEdge,
): [Point2, Point2, Point2, Point2] {
    const a = edgeSegment(frameA, outEdge);
    const b = edgeSegment(frameB, inEdge);
    return [a[0], a[1], b[1], b[0]];
}

export function clampRectToBounds(
    r: PixelRect,
    maxW: number,
    maxH: number,
): PixelRect {
    const x = Math.max(0, Math.min(r.x, maxW - 1));
    const y = Math.max(0, Math.min(r.y, maxH - 1));
    const w = Math.max(1, Math.min(r.w, maxW - x));
    const h = Math.max(1, Math.min(r.h, maxH - y));
    return { x, y, w, h };
}

export function normalizeDragRect(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
): PixelRect {
    const x = Math.min(x0, x1);
    const y = Math.min(y0, y1);
    const w = Math.abs(x1 - x0);
    const h = Math.abs(y1 - y0);
    return { x, y, w, h };
}
